"use client";

import { useEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import {
  getBrands,
  getCoupons,
  getCreditUsers,
  getDashboard,
  getSKU,
  getUsers,
  getUsersCart,
  getWalletUsers,
} from "@/features/slices/adminSlice";
import { useAppDispatch, useAppSelector } from "@/features/store";
import { fetchAdminUser } from "@/features/slices/onboardingSlice";
import MobileSidebar from "@/components/MobileSidebar";
import B2CSidebar from "@/components/B2CSidebar";
import B2CMobileSidebar from "@/components/B2CMobileSidebar";
import {
  fetchAllAdminUsersAsync,
  fetchAllAdminUsersCartsAsync,
  fetchAllCouponAsync,
  fetchAlldealerProductSaleAsync,
  fetchAlldealerSKUSaleAsync,
  fetchAllPreOrderUserDetailsAsync,
  fetchAllProductAndSKUAsync,
  fetchAllProductInventoryAsync,
  fetchAllUsersRoleAsync,
  fetchAllUsersShippingAsync,
  getAllInventoryAsync,
  getB2cDashboard,
  getCollectionsAsync,
} from "@/features/slices/b2c/b2cAdminSlice";

export default function AdminProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const dispatch = useAppDispatch();
  const pathname = usePathname();
  const router = useRouter();
  const { bulkUserID, isAuthenticated } = useAppSelector((state) => state.auth);

  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("isSidebarOpen") === "true";
    }
    return false;
  });

  useEffect(() => {
    localStorage.setItem("isSidebarOpen", isSidebarOpen.toString());
  }, [isSidebarOpen]);

  const isB2B = pathname.startsWith("/b2b");
  const isB2C = pathname.startsWith("/b2c");

  useEffect(() => {
    const protectedRoutes = [
      "/dashboard",
      "/users",
      "/orders",
      "/products",
      "/wallets",
      "/coupons",
      "/manage-inventory",
      "/brands",
      "/user-cart",
      "/line-of-credit",
      "/select-business",
    ];

    const isProtected = protectedRoutes.includes(pathname) || isB2B || isB2C;

    if (!isAuthenticated) {
      if (!pathname.startsWith("/auth")) {
        router.push("/auth");
      }
    } else {
      setIsCheckingAuth(false);
      if (!isProtected) {
        router.push("/select-business");
      }
    }
  }, [isAuthenticated, pathname, router]);

  useEffect(() => {
    if (bulkUserID) {
      dispatch(fetchAdminUser(bulkUserID));
    }
    dispatch(getDashboard());
    dispatch(getB2cDashboard());
    dispatch(getUsersCart());
    dispatch(getUsers());
    dispatch(getCoupons());
    dispatch(getCreditUsers());
    dispatch(getWalletUsers());
    dispatch(getSKU());
    dispatch(getBrands());
    dispatch(fetchAllUsersRoleAsync());
    dispatch(fetchAllCouponAsync());
    dispatch(getAllInventoryAsync());
    dispatch(fetchAllUsersShippingAsync());
    dispatch(fetchAllProductAndSKUAsync());
    dispatch(fetchAlldealerProductSaleAsync());
    dispatch(fetchAlldealerSKUSaleAsync());
    dispatch(fetchAllAdminUsersCartsAsync());
    dispatch(fetchAllPreOrderUserDetailsAsync());
    dispatch(fetchAllAdminUsersAsync());
    dispatch(getCollectionsAsync());
    dispatch(fetchAllProductInventoryAsync());
  }, [dispatch, bulkUserID]);

  const hideShell =
    pathname.startsWith("/auth") || pathname === "/select-business";

  // Choose Sidebar components based on business type
  const DesktopSidebar = isB2C ? B2CSidebar : Sidebar;
  const MobileSidebarComp = isB2C ? B2CMobileSidebar : MobileSidebar;

  return (
    <div className="flex min-h-screen max-w-screen bg-gray-100">
      {!hideShell && !isCheckingAuth && (
        <>
          {/* Sidebar Desktop */}
          <aside
            className={`fixed left-0 top-0 h-full shadow-lg transition-all duration-300 z-55 ${
              isSidebarOpen ? "w-64" : "w-fit"
            } lg:block hidden`}
          >
            <DesktopSidebar
              isSidebarOpen={isSidebarOpen}
              toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)}
              setIsSidebarOpen={setIsSidebarOpen}
            />
          </aside>

          {/* Sidebar Mobile */}
          <aside
            className={`fixed left-0 top-0 h-screen shadow-lg transition-all duration-300 z-55 ${
              isSidebarOpen ? "w-64" : "w-fit"
            } lg:hidden block`}
          >
            <MobileSidebarComp
              isSidebarOpen={isSidebarOpen}
              toggleSidebar={() => setIsSidebarOpen(false)}
            />
          </aside>

          {/* Overlay for mobile */}
          {isSidebarOpen && (
            <div
              className="fixed inset-0 backdrop-blur-md bg-white/30 z-40 lg:hidden"
              onClick={() => setIsSidebarOpen(false)}
            ></div>
          )}
        </>
      )}

      {/* Main Layout */}
      <div
        className={`flex flex-col flex-1 transition-all duration-300 ${
          !hideShell
            ? isSidebarOpen
              ? "lg:ml-64 sm:max-w-[calc(100%-256px)]"
              : "lg:ml-[68px] lg:max-w-[calc(100%-68px)] max-w-screen"
            : ""
        }`}
      >
        {!hideShell && (
          <header className="sticky top-0 bg-white shadow-md z-50">
            <Header toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
          </header>
        )}

        <main
          className={`${!hideShell ? "lg:p-6 p-2" : ""} flex-1 overflow-auto`}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
